// Shared user-facing error humanizer (LitigAI implementation, the superset of all
// five frontends; SeriesAI/in-house/home auth-header rule folded in 2026-10-07).
/**
 * Translates raw backend / transport error strings into plain English a
 * non-technical user can act on.
 *
 * Two call sites feed everything a user ever sees:
 *   - lib/apis/apiErrorMessage.js  (messages pulled out of a DRF/axios error)
 *   - lib/utils/toast.js           (anything passed to the error toast)
 *
 * `humanizeErrorMessage` returns `null` when a string is machine-speak with no
 * known translation, so the caller can substitute its own friendly fallback
 * instead of pasting a stack frame or "Request failed with status code 400"
 * into the UI.
 */

export const GENERIC_ERROR_MESSAGE = "Something went wrong. Please try again.";

/** Wording for an HTTP status when the body gives us nothing usable. */
const STATUS_MESSAGES = {
  401: "Your session has expired. Please sign in again.",
  403: "You don't have access to this.",
  404: "We couldn't find what you were looking for.",
  408: "That took longer than expected. Please try again.",
  413: "That file is too large to upload. Please try a smaller file.",
  429: "Too many attempts. Please wait a few minutes and try again.",
  500: "Something went wrong on our end. Please try again in a moment.",
  502: "Something went wrong on our end. Please try again in a moment.",
  503: "The service is temporarily unavailable. Please try again in a moment.",
  504: "That took longer than expected. Please try again.",
};

export const describeHttpStatus = (status, fallback = GENERIC_ERROR_MESSAGE) =>
  STATUS_MESSAGES[Number(status)] || fallback;

const FIELD_LABELS = {
  email: "Email",
  email_address: "Email",
  username: "Email",
  password: "Password",
  password1: "Password",
  password2: "Confirm password",
  new_password: "New password",
  new_password1: "New password",
  new_password2: "Confirm new password",
  old_password: "Current password",
  first_name: "First name",
  last_name: "Last name",
  name: "Name",
  phone: "Phone number",
  phone_number: "Phone number",
  organization_name: "Organization name",
  company_name: "Company name",
  title: "Title",
  token: "Link",
  uid: "Link",
  key: "Link",
};

// Substrings that mean "this string was written for a developer, not a user".
const TECHNICAL_MARKERS = [
  "traceback (most recent call last)",
  "botocore",
  "boto3",
  "clienterror",
  "dynamodb",
  "validationexception",
  "endpointconnectionerror",
  "django.",
  "psycopg",
  "sqlstate",
  "integrityerror",
  "operationalerror",
  "doesnotexist",
  "objects.get",
  "nonetype",
  "attributeerror",
  "typeerror:",
  "keyerror",
  "valueerror:",
  "assertionerror",
  "<class ",
  "<html",
  "<!doctype",
  "stack trace",
  " at 0x",
  "request failed with status code",
  "unexpected token",
  "json.parse",
  "is not a function",
  "of undefined",
  "internal server error",
  "bad gateway",
  "service unavailable",
  "gateway timeout",
  "when calling the",
  "x-amz-request-id",
  "request id:",
];

const PASSWORD_POLICY_PARTS = [
  [/uppercase/i, "an uppercase letter"],
  [/lowercase/i, "a lowercase letter"],
  [/numeric|number/i, "a number"],
  [/symbol|special/i, "a symbol (for example ! or ?)"],
  [/not long enough|minimum length|too short/i, "at least 8 characters"],
];

const joinRequirements = (parts) => {
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(", ")}, and ${parts[parts.length - 1]}`;
};

const describePasswordPolicy = (message) => {
  const parts = PASSWORD_POLICY_PARTS.filter(([pattern]) => pattern.test(message)).map(
    ([, label]) => label,
  );
  if (parts.length === 0) {
    return "That password doesn't meet our requirements. Use at least 8 characters with an uppercase letter, a lowercase letter, and a number.";
  }
  return `Your password needs ${joinRequirements(parts)}.`;
};

/**
 * Ordered rules. The first match wins, so put specific patterns above the
 * catch-alls. `message` may be a string or a function of the raw text.
 *
 * `blob: true` marks a rule that is designed to read a raw provider dump
 * (a boto "An error occurred (SomeException) ..." string, an OAuth error
 * payload). Only those rules are consulted for developer-facing blobs -
 * otherwise a generic keyword rule hijacks the dump and answers confidently
 * about the wrong thing (e.g. reading an AccessDeniedException from a mail
 * provider as "you don't have access to this").
 */
const RULES = [
  // --- Transport -----------------------------------------------------------
  {
    // Axios' own message. Observed live 2026-08-15: /dashboard/templates showed
    // a lawyer "Request failed with status code 400" verbatim. Map it to the
    // status sentence instead. Idempotent: the produced sentence no longer
    // matches this pattern on a second pass.
    test: /request failed with status code\s*(\d{3})/i,
    message: (body) => {
      const match = /(\d{3})/.exec(body);
      return describeHttpStatus(match ? Number(match[1]) : 0);
    },
  },
  {
    // Identity/authorization mismatches are internal vocabulary ("userId does
    // not match authenticated user") and leaked to the templates surface on
    // 2026-08-15. Tell the user what to DO instead of naming our fields.
    test: /does not match (the )?authenticated (user|app)|identity mismatch/i,
    message:
      "We couldn't confirm your access to this workspace. Sign out and sign back in, then try again.",
  },
  // --- Passwords -----------------------------------------------------------
  {
    test: /did not conform with policy|invalidpasswordexception|password must have/i,
    blob: true,
    message: describePasswordPolicy,
  },
  {
    // Plural "passwords" only. Messages pass through here twice (once from the
    // API layer, once from the toast), so a singular "...password don't match"
    // pattern would swallow this rule's own output on the second pass.
    test: /two password fields did ?n.?t match|passwords (do|does) ?n.?t match|passwords do not match|password_mismatch/i,
    message: "The two passwords don't match. Please retype them.",
  },
  {
    test: /this password is too short|password is too short|password_too_short/i,
    message: "That password is too short. Please use at least 8 characters.",
  },
  {
    test: /this password is too common|password_too_common/i,
    message: "That password is too easy to guess. Please pick something less common.",
  },
  {
    test: /entirely numeric|password_entirely_numeric/i,
    message: "That password is all numbers. Please add some letters.",
  },
  {
    test: /too similar to the|password_too_similar/i,
    message: "That password is too close to your name or email. Please pick a different one.",
  },
  // --- Sign-in -------------------------------------------------------------
  {
    test: /incorrect username or password|notauthorizedexception|invalid credentials|unable to log in with provided credentials|e-?mail address and\/?or password you specified are not correct/i,
    blob: true,
    message: "That email and password don't match. Please check them and try again.",
  },
  {
    test: /not confirmed|not verified|usernotconfirmedexception|verify your email|verification email/i,
    blob: true,
    message:
      "Your email isn't confirmed yet. Check your inbox for the confirmation link, or ask us to resend it.",
  },
  {
    test: /user does not exist|usernotfoundexception|no account (was )?found|exists in cognito but not in the app/i,
    blob: true,
    message: "We couldn't find an account with that email address.",
  },
  {
    test: /already registered|usernameexistsexception|already exists with this e-?mail|user with this email already exists/i,
    blob: true,
    message: "An account with this email already exists. Try signing in instead.",
  },
  {
    test: /attempt limit exceeded|limitexceededexception|too many (requests|attempts)|throttl/i,
    blob: true,
    message: "Too many attempts. Please wait a few minutes and try again.",
  },
  {
    test: /password reset (link|token)|expiredcodeexception|codemismatchexception|invalid (value|token|uid)|token is invalid|link (is|has) expired/i,
    message: "This link is invalid or has expired. Please request a new one.",
  },
  {
    // `authentication required.*jwt|auth header` catches the GraphQL layer's
    // "Authentication required. Provide valid JWT token auth header.", which
    // reached SeriesAI company setup as a toast telling a founder to supply an
    // auth header. Same remedy as the other rules here: sign in again.
    test: /session (has )?expired|token (is )?(not valid|expired)|authentication credentials were not provided|signature has expired|authentication required|auth header|unauthenticated/i,
    message: "Your session has expired. Please sign in again.",
  },
  {
    test: /error creating cognito user|authentication service is temporarily unavailable|cognito/i,
    message: "We couldn't finish setting up your account. Please try again in a moment.",
  },
  // --- Payments and plan limits -------------------------------------------
  {
    test: /card was declined|card_declined|do_not_honor|transaction_not_allowed/i,
    message: "Your card was declined. Please try a different card, or check with your bank.",
  },
  {
    test: /insufficient_funds|insufficient funds/i,
    message: "That card doesn't have enough available funds. Please try a different card.",
  },
  {
    test: /expired_card|card has expired|expired card/i,
    message: "That card has expired. Please add an up-to-date card.",
  },
  {
    test: /incorrect_cvc|invalid_cvc|security code/i,
    message: "The card's security code doesn't look right. Please check it and try again.",
  },
  {
    test: /incorrect_number|invalid_number|card number/i,
    message: "That card number doesn't look right. Please check it and try again.",
  },
  {
    test: /no such (subscription|customer|price|plan)|subscription.*not found|couldn.?t find that subscription/i,
    message: "We couldn't find your subscription. Please refresh, or contact support if it persists.",
  },
  {
    test: /allowedactivecases|active case limit|case limit reached|case_limit|matter_limit|matters across juristai/i,
    message:
      "Your organization has used every matter in its Vienna plan. Close a matter or add more matters from Subscriptions.",
  },
  {
    test: /seat limit|seat_limit|no seats available|not enough seats/i,
    message: "Your plan has no seats left. Add a seat to invite this person.",
  },
  {
    test: /payment (is )?required|payment_required|subscription (is )?(inactive|expired|past due)|past_due/i,
    message: "Your subscription isn't active. Update your billing details to keep using this.",
  },
  // --- Uploads and files ---------------------------------------------------
  {
    test: /entitytoolarge|too large|exceeds the maximum|request entity too large|responsesizetoolarge|payload too large/i,
    blob: true,
    message: (raw) => {
      const limit = /(\d+)\s*mb/i.exec(raw);
      return limit
        ? `That file is too large. The limit is ${limit[1]} MB.`
        : "That file is too large to upload. Please try a smaller file.";
    },
  },
  {
    test: /staged_object_too_large/i,
    message: "That file is too large to upload. Please try a smaller file.",
  },
  {
    test: /staged_object_not_found|upload (did ?n.?t|was not) finish/i,
    message: "That upload didn't finish. Please try uploading the file again.",
  },
  {
    // Skipped when the message already lists what IS accepted - the motion
    // uploader says "Only PDF, TXT, DOC, DOCX, CSV, and XLSX files are
    // allowed", and replacing that with this generic list would be wrong.
    test: /(invalid file type|unsupported (file|media) type|file type not allowed)(?!.*\ballowed\b)/i,
    message: "We can't read that file type. Please upload a PDF, Word, text, CSV, or image file.",
  },
  {
    // S3 presign failures only - a plain "access denied" from our own API is a
    // permission problem, not an expired link, and is handled further down.
    test: /signaturedoesnotmatch|policy expired|request has expired|expired.?(presigned|upload) (url|link)/i,
    blob: true,
    message: "That upload link expired. Please try uploading the file again.",
  },
  {
    test: /accessdenied|access denied|permission denied|you do ?n.?t have (access|permission)|not authorized to/i,
    message: "You don't have access to this. Ask an admin on your team to grant it.",
  },
  {
    test: /no required conversations/i,
    message:
      "This case isn't fully set up yet. Please generate a conversation before uploading documents.",
  },
  // --- Connected accounts (email, calendar, Slack, Zoom) -------------------
  {
    test: /invalid_grant|refresh token (is )?(invalid|expired)|token (has )?expired|reauthenticat|re-?authoriz|aadsts\d+|invalid_client/i,
    blob: true,
    message:
      "This connection has expired. Reconnect the account to keep syncing.",
  },
  {
    test: /insufficient (scope|permission)|missing (the )?required scope|scope.*not granted|consent required/i,
    blob: true,
    message:
      "This connection is missing a permission it needs. Reconnect the account and accept all the requested permissions.",
  },
  {
    test: /revoked|access was removed|app (was )?(removed|uninstalled)/i,
    blob: true,
    message: "Access to this account was revoked. Reconnect it to resume syncing.",
  },
  {
    test: /authorization url missing|authurl missing|could ?n.?t start connection|unable to start connection/i,
    message: "We couldn't start that connection. Please try again in a moment.",
  },
  {
    test: /mailbox (not found|unavailable)|no mailbox|calendar (not found|unavailable)/i,
    message: "We couldn't reach that mailbox or calendar. Check the account and try reconnecting.",
  },
  // --- Chat / assistant ----------------------------------------------------
  {
    test: /credit_balance_exhausted|insufficient_quota|no credits remaining/i,
    blob: true,
    message: "AI assistance is temporarily unavailable. Please try again later.",
  },
  {
    test: /chat stream failed|stream (was )?interrupted|stream ended unexpectedly/i,
    message: "The answer stopped partway through. Please send the message again.",
  },
  {
    test: /model (is )?(unavailable|overloaded)|overloaded_error|service is busy/i,
    message: "The assistant is busy right now. Please try again in a moment.",
  },
  {
    test: /context length|too many tokens|maximum context/i,
    message: "That conversation is too long for one request. Start a new chat, or shorten the message.",
  },
  {
    test: /graphql (request )?failed|did not include expected data|unexpected response shape/i,
    message: "We couldn't load that just now. Please refresh and try again.",
  },
  // --- Transport / server --------------------------------------------------
  {
    test: /network ?error|network (?:is )?unavailable|err_(internet|network|connection)|failed to fetch|net::/i,
    message: "We couldn't reach the server. Check your connection and try again.",
  },
  {
    test: /timeout|timed out|econnaborted/i,
    message: "That took longer than expected. Please try again.",
  },
  // --- Generic field validation -------------------------------------------
  {
    test: /may not be blank|this field is required|cannot be (empty|blank)/i,
    message: (raw, label) => (label ? `${label} is required.` : "Please fill in every required field."),
  },
  {
    test: /enter a valid e-?mail|valid email address/i,
    message: "Please enter a valid email address.",
  },
];

// A raw dump written by a provider or a stack: only `blob: true` rules may read
// one, because a generic keyword rule would answer confidently about the wrong
// thing.
const isProviderBlob = (message) => {
  const normalized = message.toLowerCase();
  if (TECHNICAL_MARKERS.some((marker) => normalized.includes(marker))) return true;
  // "An error occurred (SomeException) when calling ..." style boto/AWS strings.
  if (/an error occurred \(/i.test(message)) return true;
  // Bare exception class names, e.g. "ValidationError" or "SomeThingException".
  if (/^[A-Za-z]+(Error|Exception)$/.test(message.trim())) return true;
  return false;
};

// Nothing here is fit to show a user. Machine codes ("staged_object_not_found",
// "expired_card") land here only after every rule has had a chance at them -
// the ones worth showing have a rule above.
const isUnpresentable = (message) =>
  // Bare machine codes are developer noise in EITHER case. The lowercase-only
  // form let SCREAMING_SNAKE codes through — "ACCESS_DENIED" reached the
  // templates surface verbatim on 2026-08-15.
  isProviderBlob(message) || /^[a-z0-9]+(_[a-z0-9]+)+$/i.test(message.trim());

const splitFieldPrefix = (message) => {
  const match = /^([A-Za-z0-9_.]{1,40}):\s*(.+)$/s.exec(message);
  if (!match) return { field: "", label: "", body: message };

  const rawField = match[1];
  const key = rawField.split(".").pop().toLowerCase();
  if (!Object.prototype.hasOwnProperty.call(FIELD_LABELS, key)) {
    return { field: "", label: "", body: message };
  }
  return { field: key, label: FIELD_LABELS[key], body: match[2] };
};

const startsWithSubject = (body) =>
  /^(this|the|that|your|please|we|it|a|an|enter|choose|pick|use)\b/i.test(body.trim());

/**
 * @param {string} message raw message from an API, exception, or toast call
 * @returns {string|null} a user-readable sentence, or null when the input is
 *   developer-facing noise the caller should replace with its own fallback.
 */
export const humanizeErrorMessage = (message) => {
  if (typeof message !== "string") return null;

  const trimmed = message
    .replace(/\s+/g, " ")
    // Some backends prefix the status onto the text ("400: Unsupported scopeType").
    .replace(/^\d{3}:\s*/, "")
    .trim();
  if (!trimmed) return null;

  const { label, body } = splitFieldPrefix(trimmed);

  const blob = isProviderBlob(body);

  for (const rule of RULES) {
    if (blob && !rule.blob) continue;
    if (rule.test.test(body)) {
      return typeof rule.message === "function" ? rule.message(body, label) : rule.message;
    }
  }

  if (isUnpresentable(body)) return null;

  // Unrecognized but readable: keep it, and name the field it belongs to so a
  // form-level message still tells the user where to look.
  if (label && !startsWithSubject(body)) return `${label}: ${body}`;
  if (label && /^this field\b/i.test(body)) {
    return body.replace(/^this field/i, label);
  }
  return body;
};

/**
 * Convenience wrapper: always returns something safe to show.
 */
export const toUserFacingMessage = (message, fallback = GENERIC_ERROR_MESSAGE) =>
  humanizeErrorMessage(message) || fallback;

export default humanizeErrorMessage;
